// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Clickable } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { getCloudinarySecureUrl, getDefaultImagePath } from "@rugby/App/Store/Selectors/GeneralSettings";
import SearchAutoCompleteProductAction from "@rugby/App/Widgets/SearchAutoCompleteProductAction";
import LazyImage, { LazyImageProps } from "@rugby/MobiusOverrides/LazyImage";
import Link, { LinkPresentationProps } from "@rugby/MobiusOverrides/Link";
import ProductQuantityOrdered from "@rugby/Overrides/Components/ProductQuantityOrdered";
import autocompleteProductsExternalStyle from "@rugby/Overrides/Widgets/Header/autocompleteProducts.style";
import { cloudinaryUrl } from "@rugby/XDL/Helpers";

import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    defaultImagePath: getDefaultImagePath(state),
    cloudinarySecureUrl: getCloudinarySecureUrl(state),
});

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    goToSearchUrl: () => void;
    extendedStyles?: AutocompleteProductsStyles;
    defaultImagePath?: string;
    cloudinarySecureUrl: string;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    productRender = (product: any) => {
        const newProduct = {
            ...product,
            canAddToCart: Boolean(product.properties["canAddToCart"].toLowerCase()),
        };

        return (
            <GridContainer
                {...(this.props.focusedItem === product
                    ? autocompleteProductsExternalStyle.focusedContainer
                    : autocompleteProductsExternalStyle.container)}
                key={product.id!}
            >
                <StyledWrapper {...autocompleteProductsExternalStyle.container}>
                    <StyledWrapper {...autocompleteProductsExternalStyle.imageContainer}>
                        <LazyImage
                            {...autocompleteProductsExternalStyle.image}
                            src={product.image}
                            altText={product.title || product.name}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...autocompleteProductsExternalStyle.productInfo}>
                        <Link
                            {...autocompleteProductsExternalStyle.titleLink}
                            onClick={() => this.props.goToUrl(product.url)}
                        >
                            {product.displayTitle}
                        </Link>
                        <SearchAutoCompleteProductAction product={newProduct}>
                            <StyledWrapper {...autocompleteProductsExternalStyle.productAction?.container}>
                                <ProductQuantityOrdered
                                    extendedStyles={autocompleteProductsExternalStyle.productAction?.qtyInput}
                                    disabled={!product.properties["canEnterQuantity"].toLowerCase()}
                                    hideLabel
                                    id={`${product.id}-qty`}
                                />
                                <ProductAddToCartButton
                                    extendedStyles={autocompleteProductsExternalStyle.productAction?.addToCart}
                                    data-test-selector={`actionsAddToCart${product.id}`}
                                />
                            </StyledWrapper>
                        </SearchAutoCompleteProductAction>
                    </StyledWrapper>
                </StyledWrapper>
            </GridContainer>
        );
    };

    render() {
        const { products, defaultImagePath, cloudinarySecureUrl } = this.props;
        return (
            <StyledWrapper>
                <StyledWrapper {...autocompleteProductsExternalStyle.headerContainerTitleStyle}>
                    <Typography
                        {...autocompleteProductsExternalStyle.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")}
                    </Typography>
                    <Link
                        onClick={this.props.goToSearchUrl}
                        {...autocompleteProductsExternalStyle.shopAllProductsStyle}
                        withIcon
                    >
                        {translate("Shop All Products")}
                    </Link>
                </StyledWrapper>
                <GridContainer {...autocompleteProductsExternalStyle.productsContainer} className="autocomplete-products-container">
                    {products.map(product => (
                        <GridItem
                            {...autocompleteProductsExternalStyle.gridItemWrapper}
                            key={`autocompleteProduct_${product.id}`}
                        >
                            <StyledWrapper {...autocompleteProductsExternalStyle.container}>
                                <Clickable
                                    onClick={() => this.props.goToUrl(product.url)}
                                    {...autocompleteProductsExternalStyle.imageContainer}
                                >
                                    <LazyImage
                                        {...autocompleteProductsExternalStyle.image}
                                        src={cloudinaryUrl(product.image, cloudinarySecureUrl, 116)}
                                        altText={product.title || product.name}
                                        defaultImagePath={defaultImagePath}
                                        className="autocomplete-products-image" 
                                    />
                                </Clickable>
                                <StyledWrapper {...autocompleteProductsExternalStyle.productInfo}>
                                    <Link
                                        {...autocompleteProductsExternalStyle.titleLink}
                                        onClick={() => this.props.goToUrl(product.url)}
                                        data-test-selector={`productId_${product.id}`}
                                        className="autocomplete-products-link"
                                    >
                                        {product.title}
                                    </Link>
                                    <SearchAutoCompleteProductAction product={product}>
                                        {product.properties["canAddToCart"].toLowerCase() === "true" && (
                                            <StyledWrapper
                                                {...autocompleteProductsExternalStyle.productAction?.container}
                                            >
                                                <ProductQuantityOrdered
                                                    extendedStyles={
                                                        autocompleteProductsExternalStyle.productAction?.qtyInput
                                                    }
                                                    disabled={!product.properties["canEnterQuantity"].toLowerCase()}
                                                    hideLabel
                                                    id={`${product.id}-qty`}
                                                />
                                                <ProductAddToCartButton
                                                    extendedStyles={
                                                        autocompleteProductsExternalStyle.productAction?.addToCart
                                                    }
                                                    data-test-selector={`actionsAddToCart${product.id}`}
                                                />
                                            </StyledWrapper>
                                        )}
                                    </SearchAutoCompleteProductAction>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);
